// src/pages/Home.jsx
import React, { useMemo } from "react";
import Container from "../components/Container.jsx";
import Hero from "../components/Hero.jsx";
import NewsHomeCarousel from "../components/NewsHomeCarousel.jsx";
import NewsGrid from "../components/NewsGrid.jsx";
import AboutEthira from "../components/AboutEthira.jsx";
import ContactBand from "../components/ContactBand.jsx";
import useArticles from "../hooks/useArticles.js";
import useGuidesList from "../hooks/useGuidesList";
import GuideDownload from "../components/GuideDownload";
import PromoRetirementBar from "../components/PromoRetirementBar.jsx";

// ✨ NEW imports
//import DownloadsBadge from "../components/DownloadsBadge";


export default function Home() {
  const guides = useGuidesList();
  const all = useArticles();

  const newsOnly = all.filter((a) => (a.category || "").toLowerCase() === "news");

  const typesGuides = useMemo(
    () =>
      guides.filter(
        (g) => (g.category || "").toLowerCase().trim() === "property types"
      ),
    [guides]
  );

  const locationGuides = useMemo(
    () =>
      guides.filter(
        (g) => (g.category || "").toLowerCase().trim() === "locations"
      ),
    [guides]
  );

  const pickRandom = (arr, n) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, n);
  };

  // Reusable card (identical layout to Guides page)
  const GuideCard = ({ g }) => {
    const title = g.title || g.name || g.heading || g.slug || "Untitled";
    const idOrSlug = g.slug || g.id || g.guid || title;
    const imageUrl = (() => {
      const v = String(g.image || g.imageUrl || "").trim();
      if (!v) return "";
      if (/^https?:\/\//i.test(v)) return v;
      if (v.startsWith("/content/")) return v;
      return `/content/images/${v}`;
    })();

    return (
      <article className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
        {imageUrl && (
          // 🔹 make the image wrapper relative and show the overlay badge
          <div className="relative">
            {/* <DownloadsBadge item={g} /> */}
            <img src={imageUrl} alt="" className="h-48 w-full object-cover" />
          </div>
        )}
        <div className="p-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#003366]">
            {g.category}
          </div>
          {/* fix Tailwind color class */}
          <h3 className="mt-1 text-lg font-semibold text-[#003366]">{title}</h3>
          {g.excerpt && <p className="mt-2 text-[#003366]">{g.excerpt}</p>}

          <div className="mt-4 flex items-center justify-between gap-3 text-sm text-[#003366]">
            {g.date ? (
              <time dateTime={g.date}>
                {new Date(g.date).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </time>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-3">
              <a
                href={`#/guide/${encodeURIComponent(idOrSlug)}`}
                className="font-semibold text-[#003366]"
              >
                Read more →
              </a>
              <GuideDownload
                guideId={idOrSlug}
                guideTitle={title}
                slug={idOrSlug}
                variant="compact"
                label="Download PDF"
              />
              {/* 🔹 inline count next to the button */}
             
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <>
      <Hero />

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/442038852754?text=Hi%20there%2C%20I%20have%20a%20question%20about%20investment%20property."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 shadow-lg"
        title="Chat on WhatsApp"
      >
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
          alt="WhatsApp"
          className="h-5 w-5"
        />
        WhatsApp
      </a>

      <AboutEthira />

      {/* Insights & e-guides carousel */}
      <section className="pt-16 sm:pt-24 pb-8 sm:pb-10 bg-slate-50">
        <Container>
          <div className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003366]">
              Insights & e-guides
            </h2>
            <p className="mt-3 text-[#003366]">
              Independent analysis and downloadable resources designed to help you make
              informed decisions.
            </p>
          </div>
          <NewsHomeCarousel items={guides} />
        </Container>
      </section>

      {/* News */}
      <NewsGrid articles={newsOnly.slice(0, 3)} heading="News" />

      {/* Property type advice */}
      <section className="">
        <Container>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#003366]">
                Property Investment Strategies
              </h2>
              <p className="mt-3 text-[#003366]">
                Learn the pros, cons and ideal profiles for common investment property
                types.
              </p>
            </div>
            <a
              href="#/types"
              className="shrink-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-[#003366] hover:bg-slate-50"
            >
              Browse all types →
            </a>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pickRandom(typesGuides, 3).map((g) => (
              <GuideCard key={g.slug || g.id || g.guid || g.title} g={g} />
            ))}
          </div>
        </Container>
      </section>

      {/* Property location advice */}
      <section className="py-16 bg-slate-50">
        <Container>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#003366]">
                Property location advice
              </h2>
              <p className="mt-3 text-[#003366]">
                High-level guidance on investor-favourite regions — drivers and demand.
              </p>
            </div>
            <a
              href="#/locations"
              className="shrink-0 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-[#003366] hover:bg-slate-50"
            >
              Browse locations →
            </a>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pickRandom(locationGuides, 3).map((g) => (
              <GuideCard key={g.slug || g.id || g.guid || g.title} g={g} />
            ))}
          </div>
        </Container>
      </section>

      <PromoRetirementBar />
      <ContactBand />
    </>
  );
}
